import { useLayoutEffect, useRef, type RefObject } from "react";
import type { Offer, ReferenceChoice } from "@/lib/structured-text";
import { cn } from "@/lib/utils";
import { roomFor, sideIn } from "./room";

/** What a reader hears for a choice: its numeral and label, or its label and what it lacks. */
function nameOf(choice: ReferenceChoice): string {
  if (choice.numeral !== undefined) return `${choice.numeral} ${choice.label}`;
  return choice.detail === undefined
    ? choice.label
    : `${choice.label}, ${choice.detail}`;
}

/** A choice's label with the letters typed so far in bold, where the label begins with them. */
function Typed({ label, typed }: { label: string; typed: string }) {
  const lead = typed.trim();
  if (lead === "" || !label.toLowerCase().startsWith(lead.toLowerCase())) {
    return <span>{label}</span>;
  }
  return (
    <span>
      <strong className="font-semibold">{label.slice(0, lead.length)}</strong>
      {label.slice(lead.length)}
    </span>
  );
}

/** The space between the line and the list, as `mt-1` draws it. */
const OFFER_GAP = 4;

/**
 * Open the list under the words, or above their line when the box that clips it, or the
 * window, has no room below and has room above: a claim near the foot of a scrolling list of
 * claims would otherwise have its offer cut off. When neither side holds it, it takes the
 * roomier one as its height and scrolls inside. Set before paint, as the side is a fact of
 * the layout and not of what is offered.
 */
function useOfferFits(
  listRef: RefObject<HTMLUListElement | null>,
  offer: Offer,
): void {
  useLayoutEffect(() => {
    const list = listRef.current;
    if (list === null) return;
    list.style.maxHeight = "";
    list.style.top = `${String(offer.at.top)}px`;
    const { top, height } = list.getBoundingClientRect();
    const bottom = top - OFFER_GAP;
    const line = { top: bottom - (offer.at.top - offer.at.lineTop), bottom };
    const { side, space } = sideIn(
      roomFor(list),
      line,
      height,
      OFFER_GAP,
      "below",
    );
    const shown = Math.min(height, space);
    if (side === "above")
      list.style.top = `${String(offer.at.lineTop - 2 * OFFER_GAP - shown)}px`;
    if (height > space) list.style.maxHeight = `${String(space)}px`;
  }, [listRef, offer]);
}

/** What the caret is offering, as a list the keyboard walks and a press takes from. */
export function Offered({
  offer,
  at,
  onChoose,
}: {
  offer: Offer;
  at: number;
  onChoose: (choice: ReferenceChoice) => void;
}) {
  const listRef = useRef<HTMLUListElement>(null);
  useOfferFits(listRef, offer);
  return (
    <ul
      ref={listRef}
      role="listbox"
      aria-label={`Things "${offer.text}" could name`}
      style={{ left: offer.at.left, top: offer.at.top }}
      // Never wider than what holds it: the rail leaves a narrow window under
      // 200px, where a fixed 290px list would run off the side of the screen.
      className="absolute z-40 mt-1 flex max-h-[232px] w-[290px] max-w-full flex-col overflow-y-auto rounded-menu border border-border bg-card p-[5px] shadow-popover"
    >
      {offer.choices.map((choice, index) => (
        <li key={choice.reference}>
          <button
            type="button"
            role="option"
            aria-selected={index === at}
            aria-label={nameOf(choice)}
            className={cn(
              "flex w-full cursor-pointer flex-wrap items-baseline gap-x-1.5 rounded-sm px-2 py-1 text-left text-[12.5px] leading-[1.3] break-words",
              index === at && "bg-muted",
            )}
            // The caret must not leave the editor, or the offer closes first.
            onMouseDown={(event) => {
              event.preventDefault();
            }}
            onClick={() => {
              onChoose(choice);
            }}
          >
            {/* One width for every row, empty for a thing with no numeral, so the titles line up. */}
            <span
              data-numeral
              aria-hidden
              className="inline-block min-w-[3ch] text-muted-foreground tabular-nums"
            >
              {choice.numeral}
            </span>
            <Typed label={choice.label} typed={offer.text} />
            {choice.detail !== undefined && (
              <span className="ml-1.5 text-muted-foreground">
                {choice.detail}
              </span>
            )}
          </button>
        </li>
      ))}
    </ul>
  );
}
